import React from 'react';
import { ComboBox, DatePicker, DatePickerInput, Dropdown, NumberInput, Tag, TextInput } from '@carbon/react';
import { Close, Filter } from '@carbon/react/icons';
import { CrudFilterField } from './defineResource';
import { OPERATORS_BY_TYPE } from './filterTypes';
import { useCrudConfig, TenantParams } from './provider';
import { FilterValue } from './urlState';
import { ReferenceLoad, useReferenceOptions } from './useReferenceOptions';

export interface CrudFilterChipProps {
  field: CrudFilterField;
  chip: FilterValue;
  /** Collapsed out of view by the bar's `+N more` cut. Hidden, not unmounted (see CrudFilterBar). */
  hidden?: boolean;
  onChange: (next: FilterValue) => void;
  onRemove: () => void;
}

/**
 * Local-safe YYYY-MM-DD, the one format the URL codec and compileFilter agree on. Built
 * from the date's own parts so a chip never shifts a day across timezones — the package
 * cannot reach for dayjs (that is the adapter's job, and only for filter day-bounds).
 */
const toYMD = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * The chip value a single-date picker reports. An empty string means "no value" — the same
 * signal the text and number editors send, and what CrudFilterBar reads to un-commit the chip
 * into a draft. An emptied picker is a real state (flatpickr reports an empty array), not an
 * absence to be ignored.
 */
export function singleDateValue(dates: Date[] | undefined): string {
  return dates?.[0] ? toYMD(dates[0]) : '';
}

const valuesOf = (chip: FilterValue): string[] => (Array.isArray(chip.value) ? chip.value : [chip.value]);

/**
 * One chip: label, operator dropdown, the type's value editor (spec §7.4), and a remove
 * button. Controlled from above — the bar owns the state, including its local drafts —
 * so this component only projects `chip` into Carbon controls and reports changes.
 */
export function CrudFilterChip({ field, chip, hidden, onChange, onRemove }: CrudFilterChipProps) {
  const { translate, useTenantParams } = useCrudConfig();
  // eslint-disable-next-line react/hooks -- injected hook, stable per provider mount.
  const params = useTenantParams();
  const operators = OPERATORS_BY_TYPE[field.type];
  const isEmpty = chip.op === 'isEmpty';

  return (
    <div className="crud-filter-chip" hidden={hidden}>
      {/* Decorative: the chip's field name is right beside it, so the icon is hidden from
          assistive tech rather than announced as a second, wordless label. */}
      <Filter size={16} className="crud-filter-chip__icon" aria-hidden />
      <span className="crud-filter-chip__label">{translate(field.labelKey)}</span>
      <Dropdown
        id={`crud-filter-op-${field.field}`}
        className="crud-filter-chip__op"
        size="lg"
        hideLabel
        titleText={translate(field.labelKey)}
        items={[...operators]}
        itemToString={(operator) => (operator ? translate(operator.labelKey) : '')}
        selectedItem={operators.find((operator) => operator.id === chip.op) ?? null}
        onChange={({ selectedItem }) => {
          if (selectedItem) {
            onChange({ ...chip, op: selectedItem.id });
          }
        }}
      />
      {!isEmpty && <ValueEditor field={field} chip={chip} params={params} onChange={onChange} />}
      <button
        type="button"
        className="crud-filter-chip__remove"
        aria-label={translate('common.filters.remove')}
        onClick={onRemove}
      >
        <Close size={16} aria-hidden />
      </button>
    </div>
  );
}

type ValueEditorProps = {
  field: CrudFilterField;
  chip: FilterValue;
  params: TenantParams;
  onChange: (next: FilterValue) => void;
};

/**
 * The §7.4 value control, per type. `in` operators on enum/reference accumulate a list:
 * Carbon 1.35.0 has no multi-select ComboBox, so the honest primitive is a ComboBox that
 * adds to the array on selection, with the accumulated values shown as removable tags.
 */
function ValueEditor({ field, chip, params, onChange }: ValueEditorProps) {
  const { translate } = useCrudConfig();
  switch (field.type) {
    case 'text':
      return (
        <TextInput
          id={`crud-filter-value-${field.field}`}
          className="crud-filter-chip__value"
          size="lg"
          hideLabel
          labelText={`${translate(field.labelKey)} ${translate('common.value')}`}
          value={String(chip.value ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...chip, value: event.target.value })}
        />
      );
    case 'number':
      return <NumberEditor field={field} chip={chip} onChange={onChange} />;
    case 'date':
      return <DateEditor field={field} chip={chip} onChange={onChange} />;
    case 'boolean':
      return (
        <Dropdown
          id={`crud-filter-value-${field.field}`}
          className="crud-filter-chip__value"
          size="lg"
          hideLabel
          titleText={`${translate(field.labelKey)} ${translate('common.value')}`}
          items={[{ id: 'true' }, { id: 'false' }]}
          itemToString={(item) =>
            item ? translate(item.id === 'true' ? 'common.filters.true' : 'common.filters.false') : ''
          }
          selectedItem={chip.value ? { id: String(chip.value) } : null}
          onChange={({ selectedItem }) => {
            if (selectedItem) {
              onChange({ ...chip, value: selectedItem.id });
            }
          }}
        />
      );
    case 'enum':
      return (
        <OptionEditor
          field={field}
          chip={chip}
          onChange={onChange}
          options={field.options ?? []}
          labelOf={(option) => translate(option.labelKey)}
        />
      );
    case 'reference':
      return <ReferenceEditor field={field} chip={chip} params={params} onChange={onChange} />;
  }
}

function NumberEditor({
  field,
  chip,
  onChange,
}: {
  field: CrudFilterField;
  chip: FilterValue;
  onChange: (next: FilterValue) => void;
}) {
  const { translate } = useCrudConfig();
  if (chip.op === 'between') {
    const parts = valuesOf(chip);
    const at = (index: number) => parts[index] ?? '';
    const setAt = (index: number, value: string) => {
      const next = [at(0), at(1)];
      next[index] = value;
      onChange({ ...chip, value: next });
    };
    return (
      <>
        <NumberInput
          id={`crud-filter-value-${field.field}-from`}
          className="crud-filter-chip__value crud-filter-chip__value--number"
          size="lg"
          allowEmpty
          hideLabel
          label={`${translate(field.labelKey)} ${translate('common.from')}`}
          value={at(0)}
          onChange={(_event, state: { value: string | number }) => setAt(0, String(state.value))}
        />
        <NumberInput
          id={`crud-filter-value-${field.field}-to`}
          className="crud-filter-chip__value crud-filter-chip__value--number"
          size="lg"
          allowEmpty
          hideLabel
          label={`${translate(field.labelKey)} ${translate('common.to')}`}
          value={at(1)}
          onChange={(_event, state: { value: string | number }) => setAt(1, String(state.value))}
        />
      </>
    );
  }
  return (
    <NumberInput
      id={`crud-filter-value-${field.field}`}
      className="crud-filter-chip__value crud-filter-chip__value--number"
      size="lg"
      allowEmpty
      hideLabel
      label={`${translate(field.labelKey)} ${translate('common.value')}`}
      value={String(chip.value ?? '')}
      onChange={(_event, state: { value: string | number }) => onChange({ ...chip, value: String(state.value) })}
    />
  );
}

function DateEditor({
  field,
  chip,
  onChange,
}: {
  field: CrudFilterField;
  chip: FilterValue;
  onChange: (next: FilterValue) => void;
}) {
  const { translate } = useCrudConfig();
  if (chip.op === 'between') {
    const parts = valuesOf(chip);
    return (
      <DatePicker
        datePickerType="range"
        dateFormat="Y-m-d"
        value={[parts[0] ?? '', parts[1] ?? '']}
        onChange={(dates: Date[]) =>
          onChange({ ...chip, value: [dates?.[0] ? toYMD(dates[0]) : '', dates?.[1] ? toYMD(dates[1]) : ''] })
        }
      >
        <DatePickerInput
          id={`crud-filter-value-${field.field}-from`}
          labelText={`${translate(field.labelKey)} ${translate('common.from')}`}
          hideLabel
          size="lg"
          className="crud-filter-chip__value"
          placeholder="YYYY-MM-DD"
        />
        <DatePickerInput
          id={`crud-filter-value-${field.field}-to`}
          labelText={`${translate(field.labelKey)} ${translate('common.to')}`}
          hideLabel
          size="lg"
          className="crud-filter-chip__value"
          placeholder="YYYY-MM-DD"
        />
      </DatePicker>
    );
  }
  return (
    <DatePicker
      datePickerType="single"
      dateFormat="Y-m-d"
      value={chip.value ? String(chip.value) : undefined}
      onChange={(dates: Date[]) => onChange({ ...chip, value: singleDateValue(dates) })}
    >
      <DatePickerInput
        id={`crud-filter-value-${field.field}`}
        labelText={`${translate(field.labelKey)} ${translate('common.value')}`}
        hideLabel
        size="lg"
        className="crud-filter-chip__value"
        placeholder="YYYY-MM-DD"
      />
    </DatePicker>
  );
}

/**
 * The shared shape behind enum and reference: a ComboBox over fixed options, plus the
 * accumulating-tags variant when the operator is `in`.
 */
function OptionEditor({
  field,
  chip,
  onChange,
  options,
  labelOf,
}: {
  field: CrudFilterField;
  chip: FilterValue;
  onChange: (next: FilterValue) => void;
  options: { id: string; [key: string]: unknown }[];
  labelOf: (option: any) => string;
}) {
  const { translate } = useCrudConfig();

  if (chip.op === 'in') {
    const values = valuesOf(chip).filter((value) => value !== '');
    return (
      <div className="crud-filter-chip__values">
        {values.map((value) => {
          const option = options.find((candidate) => String(candidate.id) === value);
          return (
            <Tag
              key={value}
              filter
              onClose={() => onChange({ ...chip, value: values.filter((candidate) => candidate !== value) })}
            >
              {option ? labelOf(option) : value}
            </Tag>
          );
        })}
        <ComboBox
          id={`crud-filter-value-${field.field}`}
          className="crud-filter-chip__value crud-filter-chip__combo"
          size="lg"
          titleText={`${translate(field.labelKey)} ${translate('common.value')}`}
          // Without the underline the empty accumulator field is invisible — a bare chevron
          // floating under the tags. The placeholder is what says it still takes input.
          placeholder={translate('common.filters.addValue')}
          items={options}
          itemToString={(option) => (option ? labelOf(option) : '')}
          // Deliberately unselected: the ComboBox adds to the accumulated list and stays
          // empty, ready for the next pick — the multi-select 1.35.0 does not have.
          selectedItem={null}
          onChange={({ selectedItem }) => {
            if (selectedItem && !values.includes(String(selectedItem.id))) {
              onChange({ ...chip, value: [...values, String(selectedItem.id)] });
            }
          }}
        />
      </div>
    );
  }

  return (
    <ComboBox
      id={`crud-filter-value-${field.field}`}
      className="crud-filter-chip__value crud-filter-chip__combo"
      size="lg"
      titleText={`${translate(field.labelKey)} ${translate('common.value')}`}
      items={options}
      itemToString={(option) => (option ? labelOf(option) : '')}
      selectedItem={options.find((option) => String(option.id) === chip.value) ?? null}
      onChange={({ selectedItem }) => {
        onChange({ ...chip, value: selectedItem ? String(selectedItem.id) : '' });
      }}
    />
  );
}

function ReferenceEditor({
  field,
  chip,
  params,
  onChange,
}: {
  field: CrudFilterField;
  chip: FilterValue;
  params: TenantParams;
  onChange: (next: FilterValue) => void;
}) {
  // The load is a module constant in the resource's config, so it satisfies the reference
  // cache's identity contract — and shares that cache with the reference display column.
  const { optionsById } = useReferenceOptions(field.load as ReferenceLoad, params);
  const options = Array.from(optionsById.values());
  return (
    <OptionEditor
      field={field}
      chip={chip}
      onChange={onChange}
      options={options.map((option) => ({
        ...(option as Record<string, unknown>),
        id: String((option as { id?: unknown }).id),
      }))}
      labelOf={field.optionLabel ?? ((option: any) => String(option?.name ?? option?.id ?? ''))}
    />
  );
}
